import { useState, useEffect, useCallback } from 'react';
import { personService } from '../services';
import type { CardQR, Person, PersonFormData } from '../types/person';
// import { LogIn } from 'lucide-react';

interface ImageFormData {
  imagen: File | null;
}

interface UsePersonResult {
  data: Person[];
  loading: boolean;
  error: string | null;
  total: number;
  totalPages: number;
  refetch: () => void;
  search: (searchParams: string, typeParams: string) => void,
  updateStatus: (statusId: number, employee_id: string) => Promise<void>;
  createPersonComplete: (formData: PersonFormData) => Promise<{ success: boolean, personId?: string, message?: string }>;
  addImageForm: (formData: ImageFormData, personId: string) => Promise<{ success: boolean; message?: string; }>;
  updating: boolean;
  creating: boolean;
  searching: boolean;
  creatingPhoto: boolean;

  onGenerateQR: (employeeId: string, validationDate: string) => Promise<{ success: boolean, message?: string }>;
  onDownloadQR: (accessId: string) => Promise<{ success: boolean, message?: string }>;
  onLoadAccesses: (personId: string) => Promise<{ success: boolean, data?: CardQR[] }>;
  onDeleteQR: (qrId: number) => Promise<{ success: boolean, message?: string }>;
  // onLoadAccesses: (personId: string) => Promise<{ success: boolean }>;
  onGenerateAccess: (employeeId: string, visitPerson: string, authPerson: string, areaToVisit: string, pisoToVisit:string, visitPurpose?: string) => Promise<{ success: boolean, message?: string }>;
  generatingQR: boolean;
  deletingQR: boolean;
  generatingAccess: boolean;
}

interface PersonParams {
  status_persona_id?: number;
  page?: number;
  name_persona?: string;
  pageSize?: number,
  tipo_persona_id?: string,
}

// interface UserFormResponse {
//   success: boolean,
//   personId: string
// }

export const usePerson = (params: PersonParams): UsePersonResult => {
  const [data, setData] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [updating, setUpdating] = useState(false);
  const [creating, setCreating] = useState(false);
  const [generatingQR, setGeneratingQR] = useState(false);
  const [deletingQR, setDeletingQR] = useState(false);
  const [creatingPhoto] = useState(false);
  const [searching] = useState(false);
  const [generatingAccess, setGeneratingAccess] = useState(false);

  const fetchPerson = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await personService.getVisitors({ page: params.page, tipo_persona_id: params.tipo_persona_id });
      setData(response.data);
      setTotal(response.pagination.totalItems);
      setTotalPages(response.pagination.totalPages);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error fetching empleados:', err);
    } finally {
      setLoading(false);
    }
  }, [params.page, params.status_persona_id, params.name_persona]);

  useEffect(() => {
    fetchPerson();
  }, [fetchPerson]);

  const refetch = useCallback(async () => {
    try {
      setLoading(true)
      setError(null);
      const syncFacialResult = await personService.syncFacial('1');
      if (!syncFacialResult) {
        throw new Error('Error al actualizar facial');
      }
      fetchPerson();
    }
    catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error en actualizar';
      setError(errorMessage);
    }finally{
      setLoading(false)
    }
  }, [fetchPerson]);

  const search = useCallback(async (searchTerm: string, typeParams: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await personService.getVisitors({ name_persona: searchTerm, tipo_persona_id: typeParams });
      setData(response.data);
      setTotal(response.pagination.totalItems);
      setTotalPages(response.pagination.totalPages);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error fetching empleados:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchPerson]);

  const updateStatus = useCallback(async (statusId: number, employee_id: string) => {
    try {
      setUpdating(true);
      setError(null);
      await personService.updatePersonStatus(employee_id, statusId);
      // const syncFacialResult = await personService.syncFacial('1');
      // if (!syncFacialResult) {
      //   throw new Error('Error al actualizar despues de crear la persona');
      // }

      // Actualizar el estado local inmediatamente (optimistic update)
      setData(prevData =>
        prevData.map(person =>
          person.numero_persona === employee_id
            ? { ...person, status_persona_id: statusId.toString() }
            : person
        )
      );

      // Opcional: refetch para asegurar consistencia con el servidor
      // await fetchPerson();

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al actualizar status';
      setError(errorMessage);
      console.error('Error updating person status:', err);

      // En caso de error, revertir el cambio optimista haciendo refetch
      await fetchPerson();

      // Re-lanzar el error para que el componente pueda manejarlo
      throw err;
    } finally {
      setUpdating(false);
    }
  }, [fetchPerson]);

  // ✅ Método para ejecutar las 3 peticiones en secuencia
  const createPersonComplete = useCallback(async (
    formData: PersonFormData
  ) => {
    try {
      setCreating(true);
      setError(null);

      const personResult = await personService.createPerson(formData.nombre, formData.banco_s3);
      if (personResult.error) {
        setError(`Error al crear persona: ${personResult.error}`);
      }


      if (!personResult.data?.user) {
        // throw new Error('Error al crear la persona');
        setError(`Error al crear persona: No existe usuario`);
        return {
          success: false,
          error: 'Error al crear persona'
        };
      }

      // Esperar 2 segundos antes de sincronizar
      // await new Promise(resolve => setTimeout(resolve, 2000));

      const syncFacialResult1 = await personService.syncLastUserFacial('1');
      if(syncFacialResult1.error){
        setError(`Error en sync: ${syncFacialResult1.error}`);
      }

      if (!syncFacialResult1.success) {
        // throw new Error('Error al actualizar despues de crear la persona');
        setError(`No se puede sincronizar, error desconocido`);
        return {
          success: false,
          error: 'Error al actualizar despues de crear la persona'
        };
      }

      const personId = personResult.data?.user.employeeNo;

      const proveedorData = {
        generated_employee_number: personId,
        tipo_persona_id: typeof formData.tipo === 'string' ? parseInt(formData.tipo, 10) : formData.tipo,
        // tipo_persona_id: 1,
        proveedor: formData.proveedor
      }
      // 2. Agregar como proveedor

      const proveedorResult = await personService.addProveedorPerson(proveedorData);

      if(proveedorResult.error){
        setError(`Error al agregar proveedor: ${proveedorResult.error}`);
        return {
          success: false,
          error: 'Error al agregar persona'
        };
      }

      if (formData.imagen) {
        await personService.addPhotoPerson(personId, formData.imagen);
      }

      const syncFacialResult2 = await personService.syncLastUserFacial('1');
      if (!syncFacialResult2) {
        return {
          success: false,
          error: 'Error al actualizar despues de agregar foto'
        };
      }

      await fetchPerson();

      return {
        success: true,
        // personId: personId
      };

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error en el proceso completo';
      setError(errorMessage);
      console.error('Error creating person complete:', err);

      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setCreating(false);
    }
  }, [fetchPerson]);

  const addImageForm = async (formData: ImageFormData, personId: string): Promise<{ success: boolean; }> => {
    try {
      if (formData.imagen) {
        const response = await personService.addPhotoPerson(personId, formData.imagen);
        
        if (response) {
          return {
            success: true,
          };
        } else {
          return {
            success: false,
          };
        }
      } else {
        return {
          success: false,
        };
      }
    } catch (error) {
      console.error(error);

      return {
        success: false,
      };
    }
  };

  const onGenerateQR = useCallback(async (fecha_validacion: string, employee_id: string) => {
    try {
      setGeneratingQR(true);
      console.log(fecha_validacion, employee_id);
      
      // const personResult = await personService.addPersonQR(employee_id, fecha_validacion);
    } catch (e) {

    } finally {
      setGeneratingQR(false)
    }

    return { success: true, message: "Ok" }

  }, [fetchPerson]);

  const onDeleteQR = useCallback(async (qr_id: number) => {
    try {
      setDeletingQR(true);
      console.log(qr_id);
      

      // const personResult = await personService.deletePersonQR(qr_id);
    } catch (e) {

    } finally {
      setDeletingQR(false)
    }

    return { success: true, message: "Ok" }

  }, [fetchPerson]);

  const onDownloadQR = useCallback(async (accessId: string) => {
    setGeneratingQR(false)
    console.log(accessId);
     ;


    return { success: true, message: "Ok" }
  }, [fetchPerson]);

  const onLoadAccesses = useCallback(async (personId: string) => {
    try {
      setGeneratingQR(true);
      const personResult = await personService.getQRListPerson(personId);
      return {
        success: true,
        data: personResult.cards
      }
    } catch (e) {
      return {
        success: false,
      }
    } finally {
      setGeneratingQR(false)
    }

  }, [fetchPerson]);

  const onGenerateAccess = useCallback(async (employeeId: string, visitPerson: string, authPerson: string, areaToVisit: string, pisoToVisit: string, visitPurpose?: string) => {
    try {
      setGeneratingAccess(true);
      await personService.addVisita(employeeId, visitPerson, authPerson, areaToVisit, pisoToVisit, visitPurpose);

      return {
        success: true,
        message: "Ok"
      }
    } catch (e) {
      return {
        success: false
      }
    } finally {
      setGeneratingAccess(false);
    }
  }, [fetchPerson]);


  return {
    data,
    loading,
    error,
    total,
    totalPages,
    refetch,
    search,
    updateStatus,
    createPersonComplete,
    addImageForm,
    updating,
    creating,
    searching,
    creatingPhoto,
    onGenerateQR,
    onDownloadQR,
    onLoadAccesses,
    onDeleteQR,
    onGenerateAccess,
    generatingQR,
    deletingQR,
    generatingAccess
  };
};